import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATS, getSrcSet } from "./data";
import { L } from "./helpers";
import { useNav } from "./context/NavContext";
import { prefetchProducts } from "./NutriMartRoutes";
import { cdnImage, cdnSrcSet } from "@/lib/cdnImage";
import type { Lang, Product } from "./types";

interface Props {
  lang: Lang;
  P: Product[];
  setCatF: (id: string) => void;
}

/**
 * Desktop hover mega-menu surfacing all categories with a featured product.
 * Hidden on mobile via CSS (rendered inline inside TopBar after the search).
 */
export function CategoryMegaMenu({ lang, P, setCatF }: Props) {
  const { nav } = useNav();
  const [open, setOpen] = useState(false);
  const [hoverCat, setHoverCat] = useState<string>(CATS[0].id);
  const closeTimer = useRef<number | null>(null);

  const openNow = () => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
    prefetchProducts();
  };
  const closeSoon = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  };

  const featured = (P || []).find((p) => p.cat === hoverCat) || (P || [])[0];
  const go = (catId: string) => {
    setCatF(catId);
    nav("products");
    setOpen(false);
  };

  return (
    <div
      className="mega-wrap"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={closeSoon}
    >
      <button
        className="mega-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
      >
        <span>{lang === "es" ? "Categorías" : "Categories"}</span>
        <span style={{ fontSize: 9, opacity: 0.7 }}>▼</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="mega-panel"
            role="menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="mega-grid">
              <ul className="mega-cats">
                {CATS.map((c) => (
                  <li key={c.id}>
                    <button
                      role="menuitem"
                      className={`mega-cat ${hoverCat === c.id ? "active" : ""}`}
                      onMouseEnter={() => { setHoverCat(c.id); prefetchProducts(); }}
                      onFocus={() => { setHoverCat(c.id); prefetchProducts(); }}
                      onClick={() => go(c.id)}
                    >
                      <span className="mega-cat-i" aria-hidden>{c.i}</span>
                      <span>{L(c.n, lang)}</span>
                      <span className="mega-arr" aria-hidden>›</span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="mega-feat">
                {featured && (
                  <button className="mega-feat-card" onClick={() => go(hoverCat)}>
                  <img
                      src={cdnImage((featured as any).img, { width: 360 }) || (featured as any).img}
                      srcSet={cdnSrcSet((featured as any).img, [120, 240, 360]) || getSrcSet((featured as any).img)}
                      sizes="220px"
                      alt={L((featured as any).nm, lang)}
                      loading="lazy"
                      width={220}
                      height={140}
                    />
                    <div className="mega-feat-body">
                      <div className="mega-feat-tag">
                        {lang === "es" ? "Destacado" : "Featured"}
                      </div>
                      <div className="mega-feat-nm">{L((featured as any).nm, lang)}</div>
                      <div className="mega-feat-cta">
                        {lang === "es" ? "Ver categoría" : "Browse category"} →
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}